/**
 * MIRAK showcase lightbox: one shared dialog per section (not one per
 * tile) that opens on the clicked tile's slide and lets a visitor arrow
 * through every other tile in the gallery at full size, with the track
 * sliding between them. `showDialog`/`closeDialog` come from the generic
 * <dialog-component> (assets/dialog.js) already used elsewhere on the
 * site; this file only owns which slide is showing and the prev/next/
 * keyboard navigation between them.
 */

function initShowcaseLightbox(section) {
  const lightbox = section.querySelector('[data-mirak-showcase-lightbox]');
  const track = section.querySelector('[data-mirak-showcase-lightbox-track]');
  const openButtons = Array.from(section.querySelectorAll('[data-mirak-showcase-lightbox-open]'));
  if (!lightbox || !track || !openButtons.length) return;

  const slides = Array.from(track.querySelectorAll('[data-mirak-showcase-lightbox-slide]'));
  const prevButton = section.querySelector('[data-mirak-showcase-lightbox-prev]');
  const nextButton = section.querySelector('[data-mirak-showcase-lightbox-next]');
  const counter = section.querySelector('[data-mirak-showcase-lightbox-counter]');
  let index = 0;

  const pauseVideoIn = (slide) => {
    const video = slide?.querySelector('video');
    if (video && !video.paused) video.pause();
  };

  const playVideoIn = (slide) => {
    const video = slide?.querySelector('video');
    if (video) video.play().catch(() => {});
  };

  const render = () => {
    // Percentages in `transform` resolve against the element's own laid-out
    // box, not its overflowed content — and the track's own box is exactly
    // one slide wide (its extra slides overflow it, clipped by
    // `overflow: hidden`). So shifting by one slide really is a flat
    // -100% per step, not -100%/slideCount. (Verified empirically after an
    // earlier "fix" here turned out to be based on the wrong assumption
    // and broke this.)
    track.style.transform = `translateX(-${index * 100}%)`;
    if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
  };

  const goTo = (nextIndex) => {
    const wrapped = (nextIndex + slides.length) % slides.length;
    if (wrapped === index) return;
    pauseVideoIn(slides[index]);
    index = wrapped;
    render();
    playVideoIn(slides[index]);
  };

  openButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const requestedIndex = Number(button.getAttribute('data-mirak-showcase-lightbox-index')) || 0;
      index = requestedIndex;
      render();
      lightbox.showDialog();
      playVideoIn(slides[index]);
    });
  });

  prevButton?.addEventListener('click', () => goTo(index - 1));
  nextButton?.addEventListener('click', () => goTo(index + 1));

  lightbox.addEventListener('dialog:close', () => {
    slides.forEach(pauseVideoIn);
  });

  lightbox.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') goTo(index - 1);
    if (event.key === 'ArrowRight') goTo(index + 1);
  });

  render();
}

document.querySelectorAll('.mirak-showcase').forEach(initShowcaseLightbox);
